/**
 * ServiceCard Molecule Component
 * Card for displaying additional services/insurance in reservation step 2
 */

'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { useState } from 'react'

interface ServiceCardProps {
	title: string
	description: string
	price: number
	priceType?: 'total' | 'daily'
	isSelected?: boolean
	onToggle?: () => void
	className?: string
}

export function ServiceCard({
	title,
	description,
	price,
	priceType = 'total',
	isSelected = false,
	onToggle,
	className,
}: ServiceCardProps) {
	const [isHovered, setIsHovered] = useState(false)

	const formatCurrency = (amount: number) => {
		return new Intl.NumberFormat('es-CO', {
			style: 'currency',
			currency: 'COP',
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(amount)
	}

	return (
		<Card
			className={cn(
				'border-0 rounded-0! rounded-b-0! transition-all duration-200 h-full flex flex-col',
				isSelected ? 'bg-gray-100' : 'bg-gray-200',
				className
			)}
		>
			<CardContent className='p-6 flex flex-col flex-1 justify-between gap-4'>
				<div className='space-y-3'>
					{/* Title */}
					<h3 className='font-["Bricolage_Grotesque"] text-lg font-bold text-[#09194D] leading-tight'>
						{title}
					</h3>

					{/* Description */}
					<p className='text-sm text-[#09194D]/70 leading-relaxed'>
						{description}
					</p>
				</div>

				<div className='space-y-4'>
					{/* Price */}
					<div className='flex items-baseline'>
						<span className='font-["Bricolage_Grotesque"] text-2xl font-black text-[#09194D]'>
							{formatCurrency(price)}
						</span>
						{priceType === 'total' && (
							<span className='text-lg text-[#09194D] ml-1'>/ total</span>
						)}
						{priceType === 'daily' && (
							<span className='text-lg text-[#09194D] ml-1'>/ día</span>
						)}
					</div>

					{/* Button */}
					<Button
						onClick={onToggle}
						onMouseEnter={() => setIsHovered(true)}
						onMouseLeave={() => setIsHovered(false)}
						variant={isSelected ? 'default' : 'outline'}
						className={cn(
							'w-full font-["Bricolage_Grotesque"] font-bold text-base py-6 rounded-full transition-all cursor-pointer',
							isSelected
								? 'bg-[#09194D] hover:bg-[#09194D] text-white border-2 border-[#09194D]'
								: 'bg-transparent hover:bg-[#09194D] hover:text-white border-2 border-[#09194D]'
						)}
					>
						{isSelected ? (isHovered ? 'Eliminar' : 'Añadido') : 'Añadir'}
					</Button>
				</div>
			</CardContent>
		</Card>
	)
}
